/**
 * fieldVisibilityUtils.ts
 *
 * Utilities for determining field visibility and cleaning up answers for hidden fields.
 * Handles both field-level and page-level conditional visibility.
 *
 * Key Functions:
 * - cleanupHiddenFieldAnswers() - Remove answers for hidden/conditional fields recursively
 * - isFieldVisible() - Determine if a field should be shown
 * - removeHiddenFieldAnswers() - Simple removal of specified field answers
 */

import { evaluateFieldConditions } from "../../utils/fieldConditions";
import { evaluatePageConditions, findNextVisiblePage } from "../../utils/pageConditions";
import { formPagesToPages } from "../../utils/typeConversion";
import { FormVersionDetails, FormPage, FormSection as FormSectionType } from "../../types/form-types";
import { Field, Page } from "../../types/types";

// Runtime type for form version with converted fields
interface RuntimeFormVersionDetails extends Omit<FormVersionDetails, 'form_structure'> {
  form_structure: {
    pages: FormPage[];
    fields: Field[];
    sections?: FormSectionType[];
  };
}

/**
 * Cleans up answers for fields that are no longer visible based on their conditions.
 * This includes both field conditions AND page visibility.
 * This is called recursively as one field's visibility change might affect others.
 * @param currentAnswers - Map of current answers
 * @param formVersion - Form version with structure info (accepts both API and runtime types)
 * @returns Cleaned answers map with hidden field answers removed
 */
export const cleanupHiddenFieldAnswers = (currentAnswers: Map<string, any>, formVersion: FormVersionDetails | RuntimeFormVersionDetails | null): Map<string, any> => {
  if (!formVersion?.form_structure?.fields || !formVersion?.form_structure?.pages) {
    return currentAnswers;
  }

  const allFields = formVersion.form_structure.fields as Field[]; // Already converted in RuntimeFormVersionDetails
  const allPages = formVersion.form_structure.pages;
  const typedFields = allFields;
  const typedPages = formPagesToPages(allPages);
  const newAnswers = new Map(currentAnswers);
  let changed = true;

  // Convert Map to Record once for evaluateFieldConditions
  const getValuesRecord = (answersMap: Map<string, any>) => {
    const record: Record<string, any> = {};
    answersMap.forEach((val: any, key: string) => { record[key] = val; });
    return record;
  };

  // Run until no more changes happen (recursive dependencies)
  let iterations = 0;
  const MAX_ITERATIONS = 50; // Safety break for circular dependencies

  while (changed && iterations < MAX_ITERATIONS) {
    iterations++;
    changed = false;
    const currentValues = getValuesRecord(newAnswers);

    for (const field of typedFields) {
      // Skip true hidden fields (type === 'hidden') - they are metadata/system fields
      // and should not have their values cleared based on UI visibility conditions.
      if (field.type === 'hidden') continue;

      if (!newAnswers.has(field.id)) continue;

      // Check both field conditions AND page visibility
      let isFieldVisible = true;

      // Check if field page is visible
      if (field.pageId) {
        const fieldPage = typedPages.find((p) => p.id === field.pageId);
        if (fieldPage) {
          isFieldVisible = evaluatePageConditions(fieldPage, currentValues, typedFields);
        }
      }

      // Check if field conditions are met
      if (isFieldVisible && field.conditions && field.conditions.length > 0) {
        isFieldVisible = evaluateFieldConditions(field, currentValues, typedFields);
      }

      // If field should NOT be shown based on current values
      if (!isFieldVisible) {
        newAnswers.delete(field.id);
        changed = true;
      }
    }
  }

  return newAnswers;
};

/**
 * Determine if a field should be visible based on current form values
 * @param field - Field to check
 * @param answersRecord - Current form answers as record
 * @param allFields - All fields in the form
 * @param allPages - All pages in the form (optional)
 * @returns True if field should be visible
 */
export const isFieldVisible = (
  field: Field,
  answersRecord: Record<string, any>,
  allFields: Field[],
  allPages?: FormPage[]
): boolean => {
  // Never show true hidden fields (type === 'hidden')
  if (field.type === 'hidden') {
    return false;
  }

  // Evaluate conditional logic if present
  if (field.conditions && field.conditions.length > 0) {
    return evaluateFieldConditions(field, answersRecord, allFields);
  }

  // Show field by default if no conditions
  return true;
};

/**
 * Remove answers for specific field IDs
 * @param answersMap - Current answers map
 * @param fieldIdsToRemove - Set or array of field IDs to remove
 * @returns Map with specified fields removed
 */
export const removeHiddenFieldAnswers = (
  answersMap: Map<string, any>,
  fieldIdsToRemove: Set<string> | string[]
): Map<string, any> => {
  const toRemove = new Set(fieldIdsToRemove);
  const filtered = new Map(answersMap);

  toRemove.forEach(fieldId => {
    filtered.delete(fieldId);
  });

  return filtered;
};

/**
 * Get all field IDs that are hidden based on current form state
 * @param formVersion - Form version with structure (accepts both API and runtime types)
 * @param answersRecord - Current answers
 * @returns Set of hidden field IDs
 */
export const getHiddenFieldIds = (
  formVersion: FormVersionDetails | RuntimeFormVersionDetails | null,
  answersRecord: Record<string, any>
): Set<string> => {
  if (!formVersion?.form_structure?.fields || !formVersion?.form_structure?.pages) {
    return new Set();
  }

  const hidden = new Set<string>();
  const allFields = formVersion.form_structure.fields as Field[]; // Already converted in RuntimeFormVersionDetails
  const allPages = formVersion.form_structure.pages;
  const typedFields = allFields;
  const typedPages = formPagesToPages(allPages);

  for (const field of typedFields) {
    // Skip true hidden fields
    if (field.type === 'hidden') continue;

    // Check page visibility
    let isVisible = true;
    if (field.pageId) {
      const fieldPage = typedPages.find((p) => p.id === field.pageId);
      if (fieldPage) {
        isVisible = evaluatePageConditions(fieldPage, answersRecord, typedFields);
      }
    }

    // Check field conditions
    if (isVisible && field.conditions && field.conditions.length > 0) {
      isVisible = evaluateFieldConditions(field, answersRecord, typedFields);
    }

    if (!isVisible) {
      hidden.add(field.id);
    }
  }

  return hidden;
};
